/**
 * The three history sections of /licenses: the timeline, download history, and the
 * activity log.
 *
 * They are one component because they read from the same two arrays and only make
 * sense together — splitting them further would mean passing `activity` to three
 * siblings.
 *
 * SIMULATED: the dates are the literal string "Just now", written when the action
 * happened. There are no real timestamps to format yet.
 */
import { CheckCircle2, Crown, Download, FileText, ShieldCheck } from 'lucide-react';
import { COMMERCIAL_ADDON } from '../../data/products';
import { c } from '../../styles/theme';
import type { CertificateDownload, LicenseActivity } from '../../stores/licenseStore';

/** Activity-log icon per event kind. */
const ACTIVITY_ICONS: Record<string, typeof CheckCircle2> = {
  purchase: Crown,
  generate: FileText,
  download: Download,
  verify: ShieldCheck,
};

/** How many activity rows are shown. The store retains more. */
const ACTIVITY_SHOWN = 6;

export interface LicenseHistoryProps {
  /** Newest first, as the store keeps it. */
  activity: LicenseActivity[];
  /** Newest first. */
  downloadHistory: CertificateDownload[];
  /** Human-formatted issue date, or "—". Seeds the timeline's first entry. */
  issued: string;
}

export default function LicenseHistory({ activity, downloadHistory, issued }: LicenseHistoryProps) {
  // Reversed to oldest-first so the accent dot lands on the most recent event, at
  // the bottom of the timeline. The purchase is always the first thing that
  // happened, so it leads.
  const timeline = [
    { key: 'purchase', label: 'Purchased', sub: COMMERCIAL_ADDON.name, date: issued },
    ...[...activity].reverse().map((a) => ({ key: a.id, label: a.label, sub: '', date: a.date })),
  ];

  return (
    <>
      {/* Timeline */}
      <p style={{ fontSize: 12, color: c.text3, margin: '0 0 8px' }}>License timeline</p>
      <div style={{ marginBottom: 18 }}>
        {timeline.map((item, i, arr) => (
          <div key={item.key} style={{ display: 'flex', gap: 12 }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: i === arr.length - 1 ? c.accentFill : c.text3,
                  marginTop: 4,
                }}
              />
              {i < arr.length - 1 && (
                <div style={{ width: 1, flex: 1, background: c.border, minHeight: 22 }} />
              )}
            </div>
            <div style={{ paddingBottom: 14 }}>
              <p style={{ fontSize: 12, fontWeight: 500, margin: 0 }}>{item.label}</p>
              {item.sub && (
                <p style={{ fontSize: 11, color: c.text3, margin: '2px 0 0' }}>{item.sub}</p>
              )}
              <p style={{ fontSize: 10, color: c.text3, margin: '2px 0 0' }}>{item.date}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Download history */}
      <p style={{ fontSize: 12, color: c.text3, margin: '0 0 8px' }}>Download history</p>
      {downloadHistory.length === 0 ? (
        <p style={{ fontSize: 12, color: c.text3, marginBottom: 18 }}>No downloads yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 18 }}>
          {downloadHistory.map((d, i) => (
            <div
              // No stable id on a download record, and the list is
              // append-at-front only, so the index is a sound key here.
              key={`${d.date}-${d.version}-${i}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 0',
                borderBottom: `1px solid ${c.border}`,
              }}
            >
              <span style={{ fontSize: 12 }}>
                Certificate downloaded{' '}
                {i === 0 && (
                  <span style={{ color: c.accent, fontSize: 10, marginLeft: 4 }}>Latest</span>
                )}
              </span>
              <span style={{ fontSize: 11, color: c.text3 }}>
                v{d.version} · {d.date} · You
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Activity log */}
      <p style={{ fontSize: 12, color: c.text3, margin: '0 0 8px' }}>Activity log</p>
      {activity.length === 0 ? (
        <p style={{ fontSize: 12, color: c.text3, marginBottom: 18 }}>No activity yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 18 }}>
          {activity.slice(0, ACTIVITY_SHOWN).map((a) => {
            const AIcon = ACTIVITY_ICONS[a.type] ?? CheckCircle2;
            return (
              <div
                key={a.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 0',
                  borderBottom: `1px solid ${c.border}`,
                }}
              >
                <AIcon size={13} color={c.accent} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 12, flex: 1 }}>{a.label}</span>
                <span style={{ fontSize: 11, color: c.text3 }}>{a.date}</span>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
